import type { SplitImageCalloutComponent } from "../../types";
import { RuntimeImage } from "../../runtime/RendererRuntime";
import { GuardedLink } from "../../runtime/DevPlaceholder";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";

/** Split image/text callout renderer with panel variants and CTA button. */
export function SplitImageCalloutRenderer({ component }: { component: SplitImageCalloutComponent }) {
  const { imageSrc, imageAlt, title, text, ctaHref, ctaLabel, calloutVariant, panelBackground, buttonColor, imageFitTextHeight } =
    component.content;
  const { src, options } = resolveImageValue(imageSrc);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const variant = calloutVariant ?? "default";
  const legacyTheme = (component.content as { calloutTheme?: string }).calloutTheme;
  const panelBg = panelBackground ?? (legacyTheme === "light" ? "light" : "default");
  const isLightPanel = panelBg === "light";
  const isTransparentPanel = panelBg === "transparent";
  const btnColor = buttonColor ?? (legacyTheme === "light" ? "blue" : "default");
  const isBlueButton = btnColor === "blue";
  const fitHeight = imageFitTextHeight === true || String(imageFitTextHeight) === "true";

  const imageClasses = "rounded-t-md ring-1 ring-black/10 md:rounded-none md:rounded-l-md object-cover";

  // When fitHeight: enforce minimum height so the image is never 0 on mobile (inline style = no Tailwind/CSS dependency)
  const fitImageContainerStyle: React.CSSProperties | undefined = fitHeight
    ? { minHeight: "min(50vmin, 280px)" }
    : undefined;

  const grid = (
    <div className={`grid gap-0 md:grid-cols-2 ${fitHeight ? "md:items-stretch" : ""}`}>
      {fitHeight ? (
        <div
          className={mergeClassNames(
            "relative min-h-0 overflow-hidden rounded-t-md ring-1 ring-black/10 md:rounded-none md:rounded-l-md",
            overrideProps.className
          )}
          style={mergeStyles(fitImageContainerStyle, overrideProps.style)}
        >
          <RuntimeImage
            src={src}
            alt={imageAlt}
            className="absolute inset-0 h-full w-full object-cover"
            style={objectPositionStyle}
            priority
          />
        </div>
      ) : (
        <RuntimeImage
          src={src}
          alt={imageAlt}
          className={mergeClassNames(`h-full w-full ${imageClasses}`, overrideProps.className)}
          style={mergeStyles(objectPositionStyle, overrideProps.style)}
          priority
        />
      )}
      <div
        className={`flex flex-col items-start justify-center rounded-b-md p-5 text-left md:rounded-none md:rounded-r-md md:p-7 xl:p-8 ${
          isTransparentPanel
            ? "bg-transparent"
            : isLightPanel
              ? "bg-[#EDF7FC]"
              : "bg-[#005CA9] text-white"
        }`}
        style={isLightPanel || isTransparentPanel ? { color: "#0F2849" } : undefined}
      >
        <h3
          className={`text-xl font-bold leading-tight md:text-[1.35rem] xl:text-2xl ${
            isLightPanel || isTransparentPanel ? "text-[#005CA9]" : "text-white"
          }`}
          style={{ fontFamily: "var(--font-aleo)" }}
        >
          {title}
        </h3>
        {/* Interligne resserré sur mobile (24px au lieu de 28px pour 16px de corps) : ce
            paragraphe descend souvent à dix lignes en 390px, où 1,75 délayait le bloc. */}
        <p className={`mt-4 text-base leading-6 md:leading-7 xl:text-lg ${!isLightPanel && !isTransparentPanel ? "opacity-90" : ""}`}>{text}</p>
        {ctaLabel ? (
          <GuardedLink
            href={ctaHref}
            label={ctaLabel}
            className={`mt-5 inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold text-white hover:opacity-90 xl:text-base ${
              isBlueButton ? "bg-[#005CA9]" : "bg-[#F9A21B]"
            }`}
          >
            {ctaLabel}
          </GuardedLink>
        ) : null}
      </div>
    </div>
  );

  if (variant === "fullWidth") {
    const sectionBg = isTransparentPanel ? "#F3F7FC" : isLightPanel ? "#EDF7FC" : "#F3F7FC";
    return (
      <section className="mx-[calc(50%-50vw)] py-10" style={{ backgroundColor: sectionBg }}>
        <div className="mx-auto max-w-7xl px-5 md:px-8">{grid}</div>
      </section>
    );
  }

  return <section>{grid}</section>;
}

